import { config } from '@abyss/web/tools/config';
import { t } from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Convenience } from '../../../../../api/src/models/ProviderRecommendationDetails';
import { adobeLinkTrackEvent } from '../../../common/AdobeTagging/adobeLinkTrackEvent';
import { adobeModalTrackEvent } from '../../../common/AdobeTagging/adobeModalTrackEvent';
import { Constants } from '../../../common/Constants';
import { ConstantsLagoon } from '../../../common/ConstantsLagoon';
import { ScheduleModal } from '../../../common/ScheduleModal';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLagoonMessage } from '../../../hooks/useLagoonMessage';
import { StoreKeys } from '../../../hooks/useStore/state';
import { useStore } from '../../../hooks/useStore/useStore';
import {
  ProviderDetails,
  ProviderLocation,
} from '../../../models/ProviderDetails';
import { getPrimaryPhone } from '../../../utils/phone.utils';
import {
  RecommendationCriteriaMatch,
  buildFaqUrl,
  calculateDaysDifference,
  checkMatch,
  countAllMeasures,
  getMatchCount,
  getMatchDescription,
  getMatchText,
} from '../../../utils/smartChoice.utils';
import { RecommendationProp } from '../../Widget/SmartChoiceWidget/PreferencesForm';
import { defaultBooleanToFalse } from '../BenefitsBreakDown/Components/utils';
import { MatchText } from '../MatchText';
import { NotAMatchText } from '../NotAMatchText';
import {
  BreakdownContainer,
  CriteriaPointsContainer,
  CriteriaSubTitle,
  CriteriaTitle,
  DescriptionContainer,
  DrawerContentWithBackgroundgray,
  FAQContainerWithOutTopBorder,
  FAQLink,
  StyledDescription,
} from '../SmartChoiceBreakDown.styles';
import { MatchTextWithViewMore } from './Components/MatchTextWithViewMore';
import { distanceParser } from './Components/util';
import { StyledLinkViewMore } from './ConvenienceBreakDown.styles';

export interface ConvenienceBreakDownProps {
  convenience: Convenience;
  preferencesData?: RecommendationProp;
  providerLocationData?: ProviderLocation;
  providerDetails?: ProviderDetails;
  chipBackgroundColor?: string;
}

const determineAvailabilityLabel = (
  t,
  isDocasapScheduled,
  morningSchedule,
  afternoonSchedule,
  eveningSchedule
): string => {
  if (
    isDocasapScheduled === true &&
    (morningSchedule !== '' ||
      afternoonSchedule !== '' ||
      eveningSchedule !== '')
  ) {
    const currentDate = new Date();

    const schedules = [
      morningSchedule,
      afternoonSchedule,
      eveningSchedule,
    ].filter(
      (schedule) => typeof schedule === 'string' && schedule.trim() !== ''
    );

    const availableDates = schedules.filter((date) => {
      const parsedDate = new Date(date as string);
      return !isNaN(parsedDate.getTime()) && parsedDate >= currentDate;
    });

    const daysDifference = availableDates.map(
      (date) => calculateDaysDifference(currentDate, date as string) + 1
    );
    const maxDaysDifference = Math.max(...daysDifference);

    if (maxDaysDifference <= 2) {
      return t('2 days');
    } else if (maxDaysDifference <= 7) {
      return t('7 days');
    } else if (maxDaysDifference <= 14) {
      return t('14 days');
    } else if (maxDaysDifference <= 30) {
      return t('30 days');
    } else {
      return '';
    }
  }
  return '';
};

export const ConvenienceBreakDown = ({
  convenience,
  preferencesData,
  providerLocationData,
  providerDetails,
  chipBackgroundColor,
}: ConvenienceBreakDownProps) => {
  const [searchParams] = useSearchParams();
  const { portalData } = useContext(PortalContext);
  const isWidget = useStore(StoreKeys?.IS_WIDGET);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isScAppointmentAvailabilityEnabled] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_SC_APPOINTMENT_AVAILABILITY,
  ]);
  const chipValueFromParms = searchParams.get('chipValue');
  const chipValue = chipValueFromParms ? chipValueFromParms : '';

  const getMessage = useLagoonMessage({
    chipValue,
    providerType: providerDetails?.providerType || '',
  });

  const [
    description,
    availability_title,
    accept_new_patients,
    appointment_availability_within,
    appointment_unavailability,
    evening_appointments_available,
    evening_appointments_unavailable,
    weekend_appointments_available,
    weekend_appointments_unavailable,
    online_appointment_scheduling_available,
    online_appointment_scheduling_unavailable,
    access_to_care,
    within_no_of_miles,
    virtual_appointments_available,
    virtual_appointments_unavailable,
    FAQs,
    schedule_now,
  ] = getMessage([
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_DESCRIPTION,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_AVALIABILITY_TITLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_ACCEPCTING_NEW_PATIENTS,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_APPOINTMENTS_ARE_AVAILABILE_WITH_IN,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_APPOINTMENTS_ARE_UN_AVAILABILE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_EVENING_APPONTMENTS_AVAILABILE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_EVENING_APPONTMENTS_UNAVAILABILE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_WEEKEND_APPONTMENTS_AVAILABILE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_WEEKEND_APPONTMENTS_UNAVAILABILE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_ONLINE_APOINTMENT_SCHEDULE_AVAILABLE_THOUGHT_MYUHC_COM,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_ONLINE_APOINTMENT_SCHEDULE_UNAVAILABLE_THOUGHT_MYUHC_COM,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_ACCESS_TO_CARE_TITLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_WITH_NUMBER_OF_MILES_YOUR_LOCATION,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_VIRTUAL_APOINTMENTS_AVAILABLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_VIRTUAL_APOINTMENTS_UNAVAILABLE,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BREAKDOWN_FAQ_TEXT,
    ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_CONVENIENCE_BREAKDOWN
      .CONVENIENCE_BREAKDOWN_SCHEDULE_NOW,
  ]);

  const [enableTotalCostOfCareProgressBar] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TOTAL_COST_OF_CARE_PROGRESS_BAR,
  ]);

  useEffect(() => {
    adobeModalTrackEvent('Smart choice convenience breakdown');
  }, []);
  const isUmr = isUmrMember(portalData?.portalName);
  const isAcceptingNewPatients = defaultBooleanToFalse(
    providerLocationData?.acceptingNewPatients
  );
  const isEveningAppointments = defaultBooleanToFalse(
    providerLocationData?.hasEveningAppointments
  );
  const isWeekendAppointments = defaultBooleanToFalse(
    providerLocationData?.hasWeekendAppointment
  );
  const isOnlineAppointmentScheduling =
    preferencesData?.onlineApptSchdlAvbl?.toLowerCase() === 'y';
  const isDocasapScheduled = providerLocationData?.isDocasapScheduled ?? '';
  const morningSchedule =
    providerLocationData?.docAsapMorningAvailabilityDate ?? '';
  const afternoonSchedule =
    providerLocationData?.docAsapAfterNoonAvailabilityDate ?? '';
  const eveningSchedule =
    providerLocationData?.docAsapEveningAvailabilityDate ?? '';
  const availabilityLabel = determineAvailabilityLabel(
    t,
    isDocasapScheduled,
    morningSchedule,
    afternoonSchedule,
    eveningSchedule
  );
  const isDistance =
    preferencesData?.distance === '' ||
    checkMatch(convenience?.memberPreferredRadius?.toLowerCase());
  const isVirtualAppointments =
    providerLocationData?.virtualCareOffered !== null &&
    (providerLocationData?.virtualCareOffered?.length ?? 0) > 0;
  const baseUrl = isUmr ? config('UMR_BASE_URL') : config('BASE_URL');
  const count = getMatchCount(
    isAcceptingNewPatients,
    isScAppointmentAvailabilityEnabled
      ? isOnlineAppointmentScheduling && availabilityLabel !== ''
      : availabilityLabel !== '',
    checkMatch(preferencesData?.evngApptAvbl) && isEveningAppointments,
    checkMatch(preferencesData?.wkndApptAvbl) && isWeekendAppointments,
    isOnlineAppointmentScheduling &&
      convenience?.onlineAppointmentScheduling === 'Y',
    isDistance,
    checkMatch(preferencesData?.virtualApptAvbl) && isVirtualAppointments
  );

  const totalCount = countAllMeasures(
    isUmr || isScAppointmentAvailabilityEnabled ? 1 : 2,
    providerLocationData?.acceptingNewPatients ?? false,
    isScAppointmentAvailabilityEnabled
      ? isOnlineAppointmentScheduling
      : availabilityLabel !== '',
    checkMatch(preferencesData?.evngApptAvbl),
    checkMatch(preferencesData?.wkndApptAvbl),
    isOnlineAppointmentScheduling,
    checkMatch(preferencesData?.virtualApptAvbl)
  );

  const replacePlaceholders = (
    text: string | undefined,
    replacements: Record<string, string>
  ): string =>
    (text || '').replace(/\{\{(.*?)}}/g, (_, key) => {
      const keys = key.split('.');
      let value: any = replacements;
      for (const k of keys) {
        value = value?.[k];
        if (value === undefined) break;
      }
      return typeof value === 'string' ? value : '';
    });

  const outOf = t('OUT_OF');
  const outOfTotal = getMatchText(
    convenience?.scoreDetails?.match as string,
    `(${count} ${outOf} ${totalCount})`
  );
  const {
    providerId = '',
    npi = '',
    providerName = '',
  } = providerDetails || {};
  const {
    locationId = '',
    isTieredProvider = false,
    docasapPracticeId = '',
    phones,
  } = providerLocationData || {};

  return (
    <BreakdownContainer data-testid="convenience-breakdown">
      <DrawerContentWithBackgroundgray
        css={
          chipBackgroundColor && enableTotalCostOfCareProgressBar
            ? { backgroundColor: chipBackgroundColor }
            : {}
        }
        direction="column"
      >
        <CriteriaTitle data-testid="match-description">
          {`${t(
            getMatchDescription(
              convenience?.scoreDetails?.match ??
                RecommendationCriteriaMatch.None
            )
          )} ${outOfTotal}`}
        </CriteriaTitle>
      </DrawerContentWithBackgroundgray>
      <DescriptionContainer data-testid="convenience-breakdown-description">
        <StyledDescription data-testid="convenience-breakdown">
          {description?.message}
        </StyledDescription>
      </DescriptionContainer>
      <CriteriaPointsContainer
        data-testid="convenience-breakdown-points"
        direction="column"
      >
        <CriteriaSubTitle data-testid="convenince-availability">
          {availability_title?.message}
        </CriteriaSubTitle>
        {isAcceptingNewPatients ? (
          <MatchText text={accept_new_patients?.message} />
        ) : (
          <NotAMatchText text={accept_new_patients?.message} />
        )}
        {!isUmr &&
          !isScAppointmentAvailabilityEnabled &&
          (availabilityLabel !== '' ? (
            <MatchText
              text={`${appointment_availability_within?.message} ${availabilityLabel}`}
            />
          ) : (
            <NotAMatchText text={appointment_unavailability?.message} />
          ))}
        {preferencesData?.evngApptAvbl === 'y' &&
          (isEveningAppointments ? (
            <MatchText text={evening_appointments_available?.message} />
          ) : (
            <NotAMatchText text={evening_appointments_unavailable?.message} />
          ))}
        {preferencesData?.wkndApptAvbl === 'y' &&
          (isWeekendAppointments ? (
            <MatchText text={weekend_appointments_available?.message} />
          ) : (
            <NotAMatchText text={weekend_appointments_unavailable?.message} />
          ))}
        {!isUmr && isOnlineAppointmentScheduling && (
          <>
            {convenience?.onlineAppointmentScheduling === 'Y' ? (
              <MatchTextWithViewMore
                text={online_appointment_scheduling_available?.message}
              />
            ) : (
              <NotAMatchText
                text={online_appointment_scheduling_unavailable?.message}
              />
            )}
            {isScAppointmentAvailabilityEnabled && (
              <>
                {availabilityLabel !== '' ? (
                  <MatchText
                    noIcon={true}
                    text={`${appointment_availability_within?.message} ${availabilityLabel}`}
                  />
                ) : (
                  <NotAMatchText
                    noIcon={true}
                    text={appointment_unavailability?.message}
                  />
                )}
                {convenience?.onlineAppointmentScheduling === 'Y' && (
                  <StyledLinkViewMore
                    css={{
                      left: '36px',
                      fontWeight: '590 !important',
                      alignSelf: 'flex-start',
                    }}
                    onClick={() => {
                      setIsScheduleOpen(true);
                    }}
                  >{`${schedule_now?.message} > `}</StyledLinkViewMore>
                )}
              </>
            )}
          </>
        )}
        <CriteriaSubTitle>{access_to_care?.message}</CriteriaSubTitle>
        {preferencesData?.distance === '' ||
        convenience?.memberPreferredRadius === 'Y' ? (
          <MatchText
            text={replacePlaceholders(within_no_of_miles?.message, {
              distance: distanceParser(providerLocationData?.distance ?? ''),
            })}
          />
        ) : (
          <NotAMatchText
            text={replacePlaceholders(within_no_of_miles?.message, {
              distance: distanceParser(providerLocationData?.distance ?? ''),
            })}
          />
        )}

        {preferencesData?.virtualApptAvbl === 'y' && (
          <>
            {isVirtualAppointments ? (
              <MatchText text={virtual_appointments_available?.message} />
            ) : (
              <NotAMatchText text={virtual_appointments_unavailable?.message} />
            )}
          </>
        )}
      </CriteriaPointsContainer>
      <FAQContainerWithOutTopBorder>
        <FAQLink
          data-testid="faq-link-convenience"
          href={buildFaqUrl(baseUrl, isWidget)}
          onClick={() => {
            adobeLinkTrackEvent({
              name: 'Faqs',
              location: 'Smart choice convenience breakdown',
            });
          }}
        >
          {FAQs?.message}
        </FAQLink>
      </FAQContainerWithOutTopBorder>
      {isScAppointmentAvailabilityEnabled && (
        <ScheduleModal
          isOpen={isScheduleOpen}
          isTieredDataCard={isTieredProvider}
          locationId={locationId}
          npi={npi}
          phone={getPrimaryPhone(phones?.phone, phones?.appointment)}
          practiceId={docasapPracticeId}
          providerId={providerId}
          providerName={providerName}
          sectionType={Constants.RESULT_SECTION.PROVIDER}
          setIsOpen={setIsScheduleOpen}
          showDirection={false}
        />
      )}
    </BreakdownContainer>
  );
};
